import supabase from '@/supabase'
import { type Car, type CarFacet, type CarFilters, type CompositeCar, type UpdateCar } from '@/types/supabase'

const emptyFacets: CarFilters = {
  brands: [],
  models: [],
  body_types: [],
  manufacturer_exterior_colors: [],
  conditions: [],
  exterior_colors: [],
  interiors: [],
  interior_colors: [],
  prices: [],
  price_min: [],
  price_max: [],
  power_hp_min: [],
  power_hp_max: [],
  visibilities: [],
  company_ids: []
}
class CarsApi {

  public fetchDistinctCarFacet = async (facetName: keyof Car): Promise<{ value: string }[]> => {
    const { data, error } = await supabase.rpc('generate_car_facets', {
      ...emptyFacets
    })
      .eq('facet', facetName)
    if (error) throw error
    return data
      .filter(facet => facet.facet_value !== 'null')
      .map(facet => ({ value: facet.facet_value }))
  }

  public fetchDistinctCarModelsForBrand = async (brand: string): Promise<{ value: string }[]> => {
    const query = supabase.rpc('generate_car_facets', {
      ...emptyFacets,
      brands: [brand]
    })
      .eq('facet', 'model')
      .gt('count', 0)
    const { data, error } = await query
    if (error) throw error
    return data
      .filter(facet => facet.facet_value !== 'null')
      .map(facet => ({ value: facet.facet_value }))
  }

  async publishCar(carDraftId: string): Promise<string> {
    const { data: carId, error } = await supabase.rpc('publish_car', { car_draft_id: carDraftId })
    if (error) throw error
    return carId
  }

  async fetchCarsCount(filters: CarFilters): Promise<number> {
    const query = supabase.from('cars')
      .select('*', { count: 'exact', head: true })
    if (filters) this.applyFilters(query, filters)
    const { count, error } = await query
    if (error) throw error
    return count ?? 0
  }

  public async fetchAllCars(page: number, limit: number | null, sortBy: SortBy<Car>, filters?: Partial<CarFilters>): Promise<CompositeCar[]> {
    let query = supabase.from('cars')
      .select(`*,
        company:companies!cars_company_id_fkey(id, company_name, website, email, phone, street, zip_code, city),
        created_by_user:users!cars_created_by_fkey(first_name, last_name, email, phone),
        read:cars_read(created_by, car_id)
      `)
    for (const { key, ascending } of sortBy.keys) {
      query = query.order(key, { ascending })
    }
    if (limit !== null) {
      query.range((page - 1) * limit, page * limit - 1)
    }
    if (filters) this.applyFilters(query, filters)
    const { data, error } = await query
    if (error) throw error
    return data.map(car => ({
      ...car,
      is_unread: car.read.length === 0
    }))
  }

  public async fetchCarFilterFacets(filters: CarFilters): Promise<CarFacet[]> {
    const { data, error } = await supabase.rpc('generate_car_facets', filters)
    if (error) throw error
    return data
  }

  public async fetchCarById(id: string): Promise<CompositeCar> {
    const { data, error } = await supabase.from('cars')
      .select(`*,
        company:companies!cars_company_id_fkey(id, company_name, website, email, phone, street, zip_code, city),
        created_by_user:users!cars_created_by_fkey(first_name, last_name, email, phone),
        read:cars_read(created_by, car_id)`)
      .eq('id', id)
      .single()
    if (error) throw error
    return {
      ...data,
      is_unread: data.read.length === 0
    }
  }

  public async fetchCarsByCompanyId(companyId: string): Promise<CompositeCar[]> {
    const { data, error } = await supabase.from('cars')
      .select(`*,
        company:companies!cars_company_id_fkey(id, company_name, website, email, phone, street, zip_code, city),
        created_by_user:users!cars_created_by_fkey(first_name, last_name, email, phone),
        read:cars_read(created_by, car_id)`)
      .eq('company_id', companyId)
      .order('brand', { ascending: true })
      .order('model', { ascending: true })
    if (error) throw error
    return data.map(car => ({
      ...car,
      is_unread: car.read.length === 0
    }))
  }

  public async fetchCarTitleById(id: string): Promise<string> {
    const { data, error } = await supabase.from('cars')
      .select('title')
      .eq('id', id)
      .single()
    if (error) throw error
    return data.title
  }

  public async deleteCarById(id: string): Promise<void> {
    const { error } = await supabase.from('cars')
      .delete()
      .eq('id', id)
    if (error) throw error
  }

  public async updateCar(id: string, car: UpdateCar): Promise<void> {
    const { error } = await supabase.from('cars')
      .update(car)
      .eq('id', id)
    if (error) throw error
  }

  public async fetchCarTitleByIds(carIds: string[]): Promise<Map<Car['id'], string>> {
    const { data, error } = await supabase.from('cars')
      .select('id, title')
      .in('id', carIds)
    if (error) throw error
    return new Map(data.map(car => [
      car.id,
      car.title
    ]))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private applyFilters(query: any, filters: Partial<CarFilters>): void {
    Object.keys(filters).forEach(key => {
      const attribute = key as keyof CarFilters
      const filterValues = filters[attribute]
      const dbFieldName = this.convertToDbFieldName(attribute)
      if (filterValues?.length) {
        // value filters
        const valueFilters = filterValues.filter((filterValue) => filterValue !== null && filterValue !== 'null' && !attribute.endsWith('_min') && !attribute.endsWith('_max'))
        if (valueFilters.length) {
          query.in(dbFieldName, valueFilters)
        }

        // null filters
        const nullFilter = filterValues.includes('null')
        if (nullFilter) {
          query.is(dbFieldName, null)
        }

        // range filters
        filterValues
          .filter(() => attribute.endsWith('_min') || attribute.endsWith('_max'))
          .forEach((filterValue) => {
            const isMin = attribute.includes('min')
            if (isMin) {
              query.gte(dbFieldName, filterValue)
            } else {
              query.lte(dbFieldName, filterValue)
            }
          })
      }
    })
  }

  private convertToDbFieldName(key: keyof CarFilters): keyof Car {
    switch (key) {
    case 'models':
      return 'model'
    case 'brands':
      return 'brand'
    case 'body_types':
      return 'body_type'
    case 'manufacturer_exterior_colors':
      return 'manufacturer_exterior_color'
    case 'conditions':
      return 'condition'
    case 'exterior_colors':
      return 'exterior_color'
    case 'interiors':
      return 'interior'
    case 'interior_colors':
      return 'interior_color'
    case 'prices':
      return 'price'
    case 'price_min':
      return 'price'
    case 'price_max':
      return 'price'
    case 'power_hp_min':
      return 'power_hp'
    case 'power_hp_max':
      return 'power_hp'
    case 'visibilities':
      return 'visibility'
    case 'company_ids':
      return 'company_id'
    }
  }
}

export default new CarsApi()
